import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

export interface PaletteItem {
  name: string;
  href: string;
  group: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const RECENT_KEY = 'palette:recent';

const loadRecent = (): string[] => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
};
const pushRecent = (href: string) => {
  const next = [href, ...loadRecent().filter((h) => h !== href)].slice(0, 5);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
};

/* Lightweight fuzzy match: every query char must appear in order; ranks
   prefix > word-start > substring > scattered. */
const score = (query: string, target: string): number => {
  const q = query.toLowerCase(), t = target.toLowerCase();
  if (!q) return 1;
  if (t.startsWith(q)) return 100;
  if (t.split(/\s+/).some((w) => w.startsWith(q))) return 80;
  if (t.includes(q)) return 60;
  let ti = 0;
  for (const ch of q) {
    ti = t.indexOf(ch, ti);
    if (ti === -1) return 0;
    ti += 1;
  }
  return 20;
};

const CommandPalette: React.FC<{ open: boolean; onClose: () => void; items: PaletteItem[] }> = ({ open, onClose, items }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const results = useMemo(() => {
    if (!query.trim()) {
      const recent = loadRecent();
      const recentItems = recent.map((h) => items.find((i) => i.href === h)).filter(Boolean) as PaletteItem[];
      const rest = items.filter((i) => !recent.includes(i.href));
      return [...recentItems, ...rest];
    }
    return items
      .map((i) => ({ i, s: score(query, i.name) + score(query, i.group) * 0.25 }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .map((x) => x.i);
  }, [query, items]);

  useEffect(() => { setActive(0); }, [query, open]);
  useEffect(() => {
    if (open) {
      setQuery('');
      window.setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  // Keep the active row in view while arrowing through results.
  useEffect(() => {
    listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const go = (item: PaletteItem) => {
    pushRecent(item.href);
    onClose();
    navigate(item.href);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter' && results[active]) { e.preventDefault(); go(results[active]); }
    else if (e.key === 'Escape') { onClose(); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-start justify-center bg-gray-900/40 p-4 pt-[12vh] backdrop-blur-[2px] animate-fade-in"
      role="dialog" aria-modal="true" aria-label="Command palette" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-pop ring-1 ring-gray-200 animate-slide-up">
        <div className="flex items-center gap-3 border-b border-gray-100 px-4">
          <MagnifyingGlassIcon className="h-5 w-5 shrink-0 text-gray-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Jump to a page…"
            aria-label="Search pages"
            className="h-14 w-full border-0 bg-transparent text-base text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-0"
          />
          <kbd className="hidden rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs text-gray-400 sm:block">esc</kbd>
        </div>
        <ul ref={listRef} className="scrollbar-thin max-h-[45vh] overflow-y-auto py-2" role="listbox">
          {!query.trim() && loadRecent().length > 0 && (
            <li aria-hidden className="px-4 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Recent</li>
          )}
          {results.map((item, idx) => (
            <li key={item.href} role="option" aria-selected={idx === active}>
              <button
                onMouseEnter={() => setActive(idx)}
                onClick={() => go(item)}
                className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm ${idx === active ? 'bg-primary-50 text-primary-900' : 'text-gray-700'}`}
              >
                <item.icon className={`h-4 w-4 shrink-0 ${idx === active ? 'text-primary-700' : 'text-gray-400'}`} />
                <span className="flex-1 truncate font-medium">{item.name}</span>
                <span className="text-xs text-gray-400">{item.group}</span>
              </button>
            </li>
          ))}
          {!results.length && <li className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">No pages match “{query}”.</li>}
        </ul>
        <div className="flex items-center gap-4 border-t border-gray-100 bg-gray-50 px-4 py-2 text-[11px] text-gray-400">
          <span><kbd className="rounded border border-gray-200 bg-white px-1">↑↓</kbd> navigate</span>
          <span><kbd className="rounded border border-gray-200 bg-white px-1">↵</kbd> open</span>
          <span><kbd className="rounded border border-gray-200 bg-white px-1">esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
